/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { selectWithDefault } from '../../slice/initiativeSurveyListSlice';
import Dashboard, { DashboardSection, DashboardSectionTitle } from '../dashboard';
import { ReportSwitcherContainer } from '../initiative/ReportSwitcherContainer';
import DownloadCard, { DownloadCardItem } from './DownloadCard';
import { frameworks, getGroup } from '@g17eco/core';
import LoadingPlaceholder from '../LoaderContainer/LoadingPlaceholder';
import { ROUTES } from '../../constants/routes';
import { getMultiScopeReport, SupportedReportCodes } from '../report-output/outputs';
import { openUpgradeModal } from '../../actions/upgradeModal';
import G17Client from '../../services/G17Client';
import { getRootOrg } from '../../selectors/initiative';
import { getAnalytics } from '../../services/analytics/AnalyticsService';
import { AnalyticsEvents } from '../../services/analytics/AnalyticsEvents';
import { Button } from 'reactstrap';
import { generateUrl } from '../../routes/util';
import { useAppDispatch, useAppSelector } from '../../reducers';
import { addSiteAlert, SiteAlertColors } from '../../slice/siteAlertsSlice';
import { SDGImpactDownloadButton } from '../report-output/sdg-impact-report';
import { SustainabilityDownloadButton } from '../report-output/sustainability-report';
import { DownloadButton } from '../button/DownloadButton';
import { GroupMessage, isGroupEnabled } from '../../constants/groups';
import { getMainDownloadCode } from '../../config/app-config';
import './styles.scss';
import { DownloadType, DownloadScopeType, VisibilityStatus, DownloadMultiScope, CardsTotals } from '../../types/download';
import { getCTDownloadScope } from '../report-output/sgx-metric-report/downloadScope';
import { UtrvStatus } from '../../constants/status';
import { convertToUtrvStatuses, DownloadSettingsType, DownloadUtrvStatus } from './util/downloadReportHandler';
import { FeaturePermissions } from '../../services/permissions/FeaturePermissions';
import { canAccessInsightsAndDownloads } from '../../selectors/user';
import NotAuthorised from '../../routes/not-authorised';
import { getSortedVersions } from '../../constants/standards-frameworks';
import { BlockingLoader } from '../loader/BlockingLoader';
import { ESG30DownloadButton } from '../report-output/esg30-report';
import SimpleTooltip from '../simple-tooltip';
import { useScopePacksFeature } from '../survey-scope/scope-packs/useScopePacksFeature';
import { UpgradeScopePack } from '@components/survey-scope/scope-packs/UpgradeScopePack';

export const INITIAL_TOTALS = { total: 0, verified: 0 }

const cards: DownloadCardItem[] = [
  {
    code: 'gri',
    name: 'GRI aligned report',
    versionGroupCode: 'gri'
  },
  {
    code: 'sasb',
    name: 'SASB aligned report',
  },
  {
    code: 'issb',
    name: 'ISSB aligned report',
  },
  {
    code: 'sdg',
    name: 'SDG aligned report',
    hideXlsx: true,
  },
  {
    code: 'tcfd',
    name: 'TCFD aligned report',
    versionGroupCode: 'tcfd'
  },
  {
    code: 'ungc',
    name: 'UNGC aligned report',
  },
  {
    code: 'ipieca',
    name: 'IPIECA aligned report',
  },
  {
    code: 'iogp',
    name: 'IOGP aligned report',
  },
];

export const otherReportCard = {
  code: 'others',
  name: 'Other aligned reports',
  icon: 'fal fa-folder-download text-ThemeIconSecondary',
};

const downloadDisplayOptions = {
  displayUserInput: true,
  displayTag: true,
};

export const getDefaultScope = (): DownloadScopeType => ({
  standards: [],
  frameworks: [],
  custom: [],
  sdg: [],
});

export const getDownloadMultiScope = (code?: string) => {
  const scopes: DownloadScopeType = getDefaultScope();

  if (code) {
    Object.keys(frameworks).includes(code) ? scopes.frameworks.push(code) : scopes.standards.push(code)
  }
  return scopes
}

export const handleSettingsChange = (
  event: React.ChangeEvent<HTMLInputElement>,
  setDownloadSettings: (settings: DownloadSettingsType) => void,
  downloadSettings: DownloadSettingsType
) => {
  switch (event.target.name) {
    case 'privacy':
      setDownloadSettings({ ...downloadSettings, visibility: event.target.value as VisibilityStatus });
      return;
    case 'status':
      const statuses = event.target.value as DownloadUtrvStatus;
      setDownloadSettings({ ...downloadSettings, status: statuses, assuranceStatus: undefined });
      return;
    case 'assuranceStatus':
      const assuranceStatus = event.target.value as DownloadUtrvStatus;
      setDownloadSettings({ ...downloadSettings, status: undefined, assuranceStatus });
      return;
    default:
      setDownloadSettings({
        ...downloadSettings,
        selectedScopes: downloadSettings.selectedScopes.map((item) =>
          item.code === event.target.name ? { ...item, checked: event.target.checked } : item
        ),
      });
  }
}

const Downloads = () => {

  const list = useAppSelector(selectWithDefault);
  const canAccessDownloads = useAppSelector(FeaturePermissions.canAccessDownloads);
  const canAccessCombinedReport = useAppSelector(FeaturePermissions.canAccessCombinedReport);
  const rootInitiative = useAppSelector(getRootOrg);
  const scopeConfig = useAppSelector(state => state.globalData.loaded ? state.globalData.data?.config?.survey.scope : undefined);
  const dispatch = useAppDispatch();
  const analytics = getAnalytics();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();

  const [cardsTotals, setCardsTotals] = useState<CardsTotals>({});
  const mainCardCode = getMainDownloadCode(rootInitiative?.appConfigCode, rootInitiative?.permissionGroup);
  const isEsGenome = mainCardCode === 'sgx_metrics';
  const { initiativeId, surveyId = '' }: { initiativeId: string, surveyId?: string } = useParams();

  const { isRestricted, scope, onUpgrade } = useScopePacksFeature({ initiativeId });

  const setMessage = useCallback((message: string) => {
    dispatch(addSiteAlert({
      content: message,
      color: SiteAlertColors.Danger,
    }));
  }, [dispatch])

  useEffect(() => {
    if (!surveyId || surveyId === 'current') {
      return;
    }
    setIsLoading(true);

    G17Client.getSurveyVerifiedCountByGroup({
      initiativeId: initiativeId,
      surveyId: surveyId,
    }).then(data => setCardsTotals(data)).catch(e => setMessage(e.message))
      .finally(() => setIsLoading(false));
  }, [setCardsTotals, setMessage, initiativeId, surveyId])

  const track = async (type: string, code?: string) => {
    return analytics.track(AnalyticsEvents.SurveyDataDownloaded, {
      initiativeId,
      surveyId: surveyId,
      source: 'downloads_page',
      type: type,
      scopeValue: code,
    })
  };

  const canAccessDownloadsByRole = useAppSelector((state) => canAccessInsightsAndDownloads(state, initiativeId));

  const downloadReport = async (code: string, downloadScope: DownloadMultiScope) => {
    if (!canAccessDownloads) {
      dispatch(openUpgradeModal(['features', 'data_download']));
      return;
    }
    const type = 'docx';
    track(type, code);

    return getMultiScopeReport(surveyId, code as SupportedReportCodes, {
      ...downloadScope,
      displayUserInput: true,
    }).catch((e) => setMessage(e.message));
  };

  const downloadXlsx = async (
    code?: string,
    visibility: VisibilityStatus = VisibilityStatus.Include,
    utrvStatus: DownloadUtrvStatus = UtrvStatus.Verified
  ) => {
    if (!surveyId) {
      return;
    }
    const type = 'xlsx';
    track(type, code);

    return G17Client.downloadSurveyData({
      surveyId: surveyId,
      type: type,
      downloadScope: {
        visibility: visibility,
        scope: getDownloadMultiScope(code),
        ...convertToUtrvStatuses(utrvStatus),
        ...downloadDisplayOptions,
      },
    }).catch(e => setMessage(e.message));
  };

  const downloadPptx = async (
    code?: string,
    visibility: VisibilityStatus = VisibilityStatus.Include,
    utrvStatus: DownloadUtrvStatus = UtrvStatus.Verified,
    config?: { debug?: boolean }
  ) => {
    return G17Client.createBackgroundReport({
      surveyId,
      type: 'pptx',
      downloadScope: {
        visibility: visibility,
        scope: getDownloadMultiScope(code),
        ...convertToUtrvStatuses(utrvStatus),
        ...downloadDisplayOptions,
      },
      ...config
    })
      .then(() => {
        dispatch(
          addSiteAlert({
            content: 'Your report is being prepared. You will receive a notification when it is ready to download.',
            timeout: 10000
          })
        )
      });
  }

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const jobId = searchParams.get('jobId');
    const taskId = searchParams.get('taskId');
    if (!jobId || !taskId) {
      return;
    }
    G17Client.getBackgroundReportUrl(surveyId, jobId, taskId)
      .then(response => {
        const { downloadUrl } = response
        if (downloadUrl) {
          window.open(downloadUrl, '_blank', '');
        } else {
          dispatch(
            addSiteAlert({
              content: 'Unable to download report. If this occurs again, please generate a new report.',
              color: SiteAlertColors.Danger,
              timeout: 10000
            })
          )
        }
      })
      .finally(() => history.replace(location.pathname));
  }, [location.search, location.pathname, dispatch, history, surveyId]);

  const downloadCsv = async (
    code?: string,
    visibility: VisibilityStatus = VisibilityStatus.Include,
    utrvStatus: DownloadUtrvStatus = UtrvStatus.Verified
  ) => {
    if (!surveyId) {
      return;
    }
    const type = 'csv';
    track(type, code);

    if (code === 'sdg') {
      // SDG contribution download
      const url = G17Client.getDownloadUrl(`reports/initiative/${initiativeId}/scorecard/${surveyId}`);
      return G17Client.downloadFile(url);
    }

    return G17Client.downloadSurveyData({
      surveyId: surveyId,
      type: type,
      downloadScope: {
        visibility: visibility,
        scope: getDownloadMultiScope(code),
        ...convertToUtrvStatuses(utrvStatus),
        ...downloadDisplayOptions,
      },
    }).catch(e => setMessage(e.message));
  }

  const downloadSimple = async (
    type: string,
    code?: string,
    visibility: VisibilityStatus = VisibilityStatus.Include,
    utrvStatus: DownloadUtrvStatus = UtrvStatus.Verified) => {
    return G17Client.downloadSurveySimple({
      surveyId,
      type: DownloadType.SimpleCsv === type ? 'csv' : 'xlsx',
      downloadScope: {
        scope: getCTDownloadScope(code),
        visibility,
        ...convertToUtrvStatuses(utrvStatus),
        ...downloadDisplayOptions,
      },
    });
  };

  const items = cards.map(card => {
    const group = getGroup('frameworks', card.code) || getGroup('standards', card.code);

    const isEnabled = isGroupEnabled(group, scopeConfig);

    return {
      ...card,
      src: group?.src,
      disabled: !isEnabled,
      tooltip: !isEnabled ? GroupMessage.Disabled : undefined
    };
  });

  const currentSurvey = list.find(item => item._id === surveyId);
  const hasSurvey = list.some(item => item._id)

  const goToCustomDownloads = () => {
    const url = generateUrl(ROUTES.DOWNLOADS_CUSTOM, { initiativeId });
    history.push(url);
  }

  const getTotals = (type: string) => {
    const card = cards.find(c => c.code === type);
    if (card?.versionGroupCode) {
      const versions = getSortedVersions(card.versionGroupCode);
      const [latestVersion, prevVersion] = versions;
      if (!!cardsTotals?.[latestVersion.code]?.total) {
        return cardsTotals?.[latestVersion.code];
      }
      return cardsTotals?.[prevVersion.code] ?? INITIAL_TOTALS;
    }
    return cardsTotals?.[type] ?? INITIAL_TOTALS;
  }

  const renderSDGImpactReport = () => {
    if (isRestricted) {
      return null;
    }

    return (
      <DownloadCard
        aside={false}
        item={{
          name: 'SDG Impact report',
          code: 'ctl',
          src: getGroup('standards', 'sdg')?.src,
        }}
        noSurvey={!hasSurvey}
        isDisabled={!hasSurvey}
        totals={getTotals('ctl')}
      >
        <SDGImpactDownloadButton
          initiativeId={initiativeId}
          surveyId={surveyId}
          downloadSimple={downloadSimple}
          downloadXlsx={downloadXlsx}
          downloadPptx={downloadPptx}
          downloadCsv={downloadCsv}
          totals={getTotals('ctl')}
          defaultStatus={UtrvStatus.Verified}
        />
      </DownloadCard>
    );
  };

  const renderForESGenome = () => {
    if (!isEsGenome) {
      return null;
    }

    return (
      <>
        <DownloadCard
          key='sgx_metrics'
          item={{
            code: 'sgx_metrics',
            name: 'Sustainability report',
            src: getGroup('standards', 'sgx_metrics')?.src,
          }}
          downloadButton={
            <SustainabilityDownloadButton
              requiredScope='sgx_metrics'
              modalTitleOptions={{
                word: { title: 'Sustainability Word report', tooltip: 'SGX and RegCo compliant Word report' },
              }}
              code={mainCardCode}
              initiativeId={initiativeId}
              surveyId={surveyId}
              disabled={!hasSurvey}
              {...downloadDisplayOptions}
            />
          }
          noSurvey={!hasSurvey}
          isDisabled={!hasSurvey}
          totals={getTotals('sgx_metrics')}
        />
        <div className='download-card'>
          <section className='download-details shadow-none border-0 d-flex flex-column'>
            <div className='download-actions d-flex flex-column mt-0'>
              <div className={'strong download-card__heading'}>Download all survey data</div>
              <div className={'mt-3 mb-2 h6 strong'}>Data report</div>
              <DownloadButton
                disabled={!hasSurvey}
                block
                color='link'
                onClick={() => downloadSimple(DownloadType.SimpleXlsx)}
              >
                <i className='fas fa-file-excel mr-2 fa-lg' />
                Download all data to Excel
              </DownloadButton>
              <DownloadButton
                disabled={!hasSurvey}
                block
                color='link'
                onClick={() => downloadSimple(DownloadType.SimpleCsv)}
              >
                <i className='fas fa-file-csv mr-2 fa-lg' />
                Download all data to CSV
              </DownloadButton>
              <Button
                className='w-100 text-center mt-4'
                color='primary'
                size='lg'
                onClick={() => goToCustomDownloads()}
              >
                Custom Reports
              </Button>
            </div>
          </section>
        </div>
      </>
    );
  };

  // render ctl specific report and all standards & frameworks report
  const renderSustainabilityReports = () => {
    if (!currentSurvey || isEsGenome || isRestricted) {
      return null;
    }

    return (
      <>
        {renderSDGImpactReport()}
        <DownloadCard
          key={mainCardCode}
          item={{
            code: mainCardCode,
            name: 'Sustainability report',
            icon: 'fa-thin fa-seedling text-ThemeSuccessMedium',
          }}
          downloadButton={
            <SustainabilityDownloadButton
              defaultStatus={UtrvStatus.Verified}
              code={mainCardCode}
              initiativeId={initiativeId}
              surveyId={surveyId}
              disabled={!hasSurvey}
              totals={getTotals('all')}
              {...downloadDisplayOptions}
            />
          }
          noSurvey={!hasSurvey}
          isDisabled={!hasSurvey}
          totals={getTotals('all')}
        />
        <DownloadCard
          aside={false}
          item={{
            name: 'Custom reports',
            code: 'custom',
            icon: 'fa-thin fa-file-chart-pie text-ThemeIconSecondary',
          }}
          noSurvey={!hasSurvey}
          isDisabled={!hasSurvey}
        >
          <DownloadButton className='doc-btn' color='secondary' disabled={!canAccessCombinedReport} outline={true} onClick={goToCustomDownloads}>
            Custom reports <i className='fas fa-file-download ml-2' />
          </DownloadButton>
        </DownloadCard>
      </>
    );
  };

  // render CT Starter reports and all standards & frameworks report
  const renderForCTStarter = () => {
    if (!currentSurvey || !isRestricted) {
      return null;
    }

    const scopePacks = [...scope.standards, ...scope.frameworks];
    const filteredItems = items.filter((item) => {
      if (scopePacks.includes(item.code)) {
        return true;
      }

      // Card can switch to a new version inside, therefore we also need to follow
      // same strategy and check if never is included, therefore we should also allow this code
      // as it will be replaced by newer groupCodeVersion
      if (item.versionGroupCode) {
        return getSortedVersions(item.versionGroupCode).some((s) => scopePacks.includes(s.code));
      }

      return false;
    });

    return (
      <>
        <DownloadCard
          item={{
            name: 'ESG Essentials report',
            code: 'esg30',
            src: getGroup('frameworks', 'esg30')?.src,
          }}
          noSurvey={!hasSurvey}
          isDisabled={!hasSurvey}
          totals={getTotals('esg30')}
        >
          <ESG30DownloadButton
            initiativeId={initiativeId}
            surveyId={surveyId}
            downloadSimple={downloadSimple}
            downloadXlsx={downloadXlsx}
            downloadPptx={downloadPptx}
            downloadCsv={downloadCsv}
            totals={getTotals('esg30')}
            defaultStatus={UtrvStatus.Verified}
          />
        </DownloadCard>
        {filteredItems.map((item) => (
          <DownloadCard
            key={item.code}
            item={item}
            downloadSimple={downloadSimple}
            downloadCsv={downloadCsv}
            downloadXlsx={downloadXlsx}
            downloadReport={downloadReport}
            noSurvey={!hasSurvey}
            isDisabled={!hasSurvey}
            totals={getTotals(item.code)}
            surveyScope={currentSurvey.scope}
          />
        ))}
        <DownloadCard
          item={{
            name: 'Custom reports',
            code: 'custom',
            icon: 'fa-thin fa-file-chart-pie text-ThemeIconSecondary',
          }}
        >
          <SimpleTooltip text='Not available in your current plan'>
            <DownloadButton className='doc-btn' color='secondary' disabled={true} outline={true} onClick={goToCustomDownloads}>
              Custom reports <i className='fas fa-file-download ml-2' />
            </DownloadButton>
          </SimpleTooltip>
        </DownloadCard>
      </>
    );
  };

  const renderStandardsFrameworksPolicies = () => {
    if (!currentSurvey) {
      return null;
    }

    if (isRestricted) {
      return (
        <div className='mt-5'>
          <UpgradeScopePack isRestricted={isRestricted} onUpgrade={onUpgrade} />
        </div>
      );
    }

    return (
      <div className='download-container mt-3 py-3 px-4'>
        <div className='mt-3 mx-2 d-flex flex-wrap justify-content-between mb-5'>
          {items.map((item) => (
            <DownloadCard
              key={item.code}
              item={item}
              downloadSimple={downloadSimple}
              downloadCsv={downloadCsv}
              downloadXlsx={downloadXlsx}
              downloadReport={downloadReport}
              noSurvey={!hasSurvey}
              isDisabled={!hasSurvey}
              totals={getTotals(item.code)}
              surveyScope={currentSurvey.scope}
            />
          ))}
          <DownloadCard
            key={otherReportCard.code}
            item={otherReportCard}
            downloadSimple={downloadSimple}
            downloadCsv={downloadCsv}
            downloadXlsx={downloadXlsx}
            downloadReport={downloadReport}
            noSurvey={!hasSurvey}
            isDisabled={!hasSurvey}
            getTotals={getTotals}
          />
        </div>
      </div>
    );
  };

  const onReportChange = useCallback(
    (surveyId: string) => {
      history.push(generateUrl(ROUTES.DOWNLOADS, { initiativeId, surveyId }));
    },
    [history, initiativeId]
  );

  if (!canAccessDownloadsByRole) {
    return <NotAuthorised />
  }

  return (
    <Dashboard className='downloads'>
      {isLoading ? <BlockingLoader /> : null}
      <DashboardSectionTitle
        title='Downloads'
        buttons={[
          <ReportSwitcherContainer
            initiativeId={initiativeId}
            selectedSurveyId={surveyId}
            onChange={onReportChange}
            showAutomaticReport={false}
            disabled={!hasSurvey}
          />,
        ]}
      />
      <DashboardSection title='Sustainability report' headingStyle={4}>
        <LoadingPlaceholder height={77} isLoading={!list} count={1} className='mt-4'>
          <div className='download-container border-0 py-3 px-4'>
            <div className='mx-2 pt-2 d-flex align-items-start flex-wrap justify-content-between'>
              {renderSustainabilityReports()}
              {renderForCTStarter()}
              {renderForESGenome()}
            </div>
          </div>
        </LoadingPlaceholder>
        {currentSurvey ? (
          <LoadingPlaceholder height={77} isLoading={!list} count={1}>
            <h4>Standards, Frameworks & Policies</h4>
            {renderStandardsFrameworksPolicies()}
          </LoadingPlaceholder>
        ) : null}
      </DashboardSection>
    </Dashboard>
  );
}

export default Downloads;
